// Utility to extract brand assets (colors, logo, typography) from client data and raw HTML

export interface ClientBranding {
  primaryColor: string; // e.g. '#2563eb' or 'rgb(37, 99, 235)'
  secondaryColor: string;
  accentColor: string;
  textColor: string;
  backgroundColor: string;
  logoUrl: string | null;
  businessName: string;
  brandInitial: string;
  fontFamily?: string;
  tagline?: string;
  hasCustomTheme: boolean;
}

// Convert hex or rgb to HSL or lighten/darken
export function adjustColorBrightness(hex: string, percent: number): string {
  const num = parseInt(hex.replace('#', ''), 16);
  if (isNaN(num)) return hex;
  const amt = Math.round(2.55 * percent);
  const R = (num >> 16) + amt;
  const G = (num >> 8 & 0x00FF) + amt;
  const B = (num & 0x0000FF) + amt;
  return '#' + (0x1000000 + (R < 255 ? (R < 1 ? 0 : R) : 255) * 0x10000 + (G < 255 ? (G < 1 ? 0 : G) : 255) * 0x100 + (B < 255 ? (B < 1 ? 0 : B) : 255)).toString(16).slice(1);
}

// Extract colors from raw HTML content
export function extractBrandingFromHtml(html: string | undefined, clientData?: any): ClientBranding {
  const businessName = clientData?.name || 'Painel do Cliente';
  const brandInitial = (businessName.charAt(0) || 'C').toUpperCase();

  // Defaults (refined neutral/indigo theme)
  let primaryColor = '#18181b'; // zinc-900 default
  let secondaryColor = '#4f46e5'; // indigo-600
  const accentColor = '#10b981'; // emerald-500
  const backgroundColor = '#FAFAFA';
  const textColor = '#0f172a';
  let logoUrl: string | null = null;
  let hasCustomTheme = false;

  if (!html) {
    return {
      primaryColor,
      secondaryColor,
      accentColor,
      textColor,
      backgroundColor,
      logoUrl,
      businessName,
      brandInitial,
      hasCustomTheme: false,
    };
  }

  try {
    // 1. Try to find Logo in HTML: img with alt or class containing logo / brand / header
    const logoImgRegex = /<img[^>]+src=["']([^"']+)["'][^>]*(alt=["'][^"']*(logo|marca|logotipo|brand)[^"']*["']|class=["'][^"']*(logo|brand|header-logo)[^"']*["'])[^>]*>/i;
    const logoMatch = html.match(logoImgRegex);
    if (logoMatch && logoMatch[1]) {
      logoUrl = logoMatch[1];
      hasCustomTheme = true;
    } else {
      // Fallback img search for first header image or logo id
      const generalLogoRegex = /<img[^>]+src=["']([^"']+)["'][^>]*id=["'][^"']*logo[^"']*["'][^>]*>/i;
      const genMatch = html.match(generalLogoRegex);
      if (genMatch && genMatch[1]) {
        logoUrl = genMatch[1];
        hasCustomTheme = true;
      }
    }

    // 2. Extract Tailwind classes or inline styles for dominant colors
    // Check for explicit hex colors (#xxxxxx) in style or Tailwind config
    const hexRegex = /#([0-9a-fA-F]{6}|[0-9a-fA-F]{3})\b/g;
    const foundHexes = html.match(hexRegex) || [];
    
    // Filter out common black/white/grays to find the real accent/brand color
    const nonNeutralHexes = foundHexes.filter(h => {
      const lower = h.toLowerCase();
      return !['#fff', '#ffffff', '#000', '#000000', '#eee', '#eeeeee', '#f8f9fa', '#f3f4f6', '#e5e7eb', '#111827', '#1f2937', '#374151', '#4b5563', '#6b7280', '#9ca3af', '#d1d5db'].includes(lower);
    });

    if (nonNeutralHexes.length > 0) {
      // Count frequency
      const freq: Record<string, number> = {};
      nonNeutralHexes.forEach(h => {
        const standard = h.length === 4 ? `#${h[1]}${h[1]}${h[2]}${h[2]}${h[3]}${h[3]}` : h.toLowerCase();
        freq[standard] = (freq[standard] || 0) + 1;
      });

      const sorted = Object.entries(freq).sort((a, b) => b[1] - a[1]);
      if (sorted.length > 0) {
        primaryColor = sorted[0][0];
        hasCustomTheme = true;
      }
      if (sorted.length > 1) {
        secondaryColor = sorted[1][0];
      }
    } else {
      // Look for Tailwind dominant color keywords: bg-blue-600, bg-emerald-600, bg-amber-600, etc.
      const twColorMap: Record<string, string> = {
        'emerald': '#059669',
        'green': '#16a34a',
        'teal': '#0d9488',
        'cyan': '#0891b2',
        'blue': '#2563eb',
        'indigo': '#4f46e5',
        'violet': '#7c3aed',
        'purple': '#9333ea',
        'fuchsia': '#c026d3',
        'pink': '#db2777',
        'rose': '#e11d48',
        'red': '#dc2626',
        'orange': '#ea580c',
        'amber': '#d97706',
        'yellow': '#ca8a04',
      };

      for (const [colorName, hexVal] of Object.entries(twColorMap)) {
        if (html.includes(`bg-${colorName}-600`) || html.includes(`text-${colorName}-600`) || html.includes(`bg-${colorName}-500`)) {
          primaryColor = hexVal;
          hasCustomTheme = true;
          break;
        }
      }
    }
  } catch (err) {
    console.warn('Could not extract branding from HTML:', err);
  }

  return {
    primaryColor,
    secondaryColor,
    accentColor,
    textColor,
    backgroundColor,
    logoUrl,
    businessName,
    brandInitial,
    hasCustomTheme,
  };
}
